import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api, { isLimitError } from '../api/api';
import Alert from './Alert';
import Icon from './Icon';
import { CopyButton } from '../screens/create/generatorUi';
import './repurpose.css';

// "Turn into…": rewrites a post, idea or text as another format with the AI
// (POST /repurpose) and shows the result in a modal with copy buttons. Ads,
// blog articles and video scripts can be opened in their create screen.

export const FORMATS = [
    { id: 'carousel', label: 'Carousel', icon: 'view_carousel', part: 'Slide', hint: 'Slides outline' },
    { id: 'script', label: 'Video script', icon: 'movie', part: 'Beat', hint: 'Hook, beats, call to action', open: { path: '/dashboard/video', label: 'Video scripts' } },
    { id: 'ad', label: 'Ad', icon: 'campaign', part: 'Field', hint: 'Headline, text, description', open: { path: '/dashboard/ads', label: 'Ad copy' } },
    { id: 'blog', label: 'Blog article', icon: 'article', part: 'Section', hint: 'Title and section outline', open: { path: '/dashboard/blog', label: 'Blog articles' } },
    { id: 'thread', label: 'Thread', icon: 'forum', part: 'Post', hint: 'X / Threads posts' },
];

const toText = (value) => (typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '');

// The server's answer in a shape that's safe to render; null when it has no parts
export const normalizeRepurpose = (raw, to) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const parts = (Array.isArray(raw.parts) ? raw.parts : [])
        .map(part => (typeof part === 'string' ? { body: part } : part))
        .filter(part => part && typeof part === 'object')
        .map(part => ({ heading: toText(part.heading), body: toText(part.body) }))
        .filter(part => part.body);
    if (!parts.length) return null;
    return {
        format: FORMATS.some(format => format.id === raw.format) ? raw.format : to,
        title: toText(raw.title),
        parts,
        cta: toText(raw.cta),
        hashtags: (Array.isArray(raw.hashtags) ? raw.hashtags : []).map(toText).filter(Boolean).slice(0, 8),
    };
};

const allText = (result) => [
    result.title,
    ...result.parts.map(part => [part.heading, part.body].filter(Boolean).join('\n')),
    result.cta,
    result.hashtags.join(' '),
].filter(Boolean).join('\n\n');

// What each create screen starts from
const seedFor = (result, source) => {
    const first = result.parts[0];
    if (result.format === 'blog') {
        return { topic: [result.title, ...result.parts.map(part => `- ${part.heading || part.body}`)].join('\n'), platform: source.platform, from: 'Turned into a blog article' };
    }
    if (result.format === 'ad') {
        const headline = result.parts.find(part => /headline/i.test(part.heading)) || first;
        return { topic: result.title, hook: headline.body, platform: source.platform, from: 'Turned into an ad' };
    }
    return { topic: result.title, hook: first.body.replace(/\s*\[On screen:[^\]]*\]\s*/gi, ' ').trim(), platform: source.platform, from: 'Turned into a video script' };
};

const RepurposeModal = ({ format, source, onClose }) => {
    const navigate = useNavigate();
    const titleId = useId();
    const [state, setState] = useState({ status: 'loading' }); // loading | done (+ result) | error (+ message)
    const modalRef = useRef(null);
    const requestId = useRef(0);
    const mounted = useRef(true);

    const run = useCallback(async () => {
        const id = ++requestId.current;
        setState({ status: 'loading' });
        try {
            const result = normalizeRepurpose(await api.repurpose(source, format.id), format.id);
            if (id !== requestId.current || !mounted.current) return;
            if (!result) throw new Error('Empty result');
            setState({ status: 'done', result });
        } catch (e) {
            if (id !== requestId.current || !mounted.current) return;
            // The upgrade prompt explains a plan limit
            if (isLimitError(e)) {
                onClose();
                return;
            }
            const status = e.response?.status;
            setState({
                status: 'error',
                message: (status === 400 && toText(e.response?.data?.error))
                    || `Couldn't write the ${format.label.toLowerCase()}. Nothing was used up, so please try again.`,
            });
        }
    }, [format, source, onClose]);

    // Once per modal (Strict Mode mounts twice in development; each run uses a generation)
    const started = useRef(false);
    useEffect(() => {
        mounted.current = true;
        if (!started.current) {
            started.current = true;
            run();
        }
        return () => {
            mounted.current = false;
        };
    }, [run]);

    useEffect(() => {
        modalRef.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const { status, result } = state;
    const isLoading = status === 'loading';
    const open = format.open;

    // In <body>, so no card's stacking or overflow can clip it
    return createPortal(
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ls-modal ls-turn-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-busy={isLoading} tabIndex={-1} ref={modalRef}>
                <div className="ls-turn-head">
                    <div className="ls-turn-heading">
                        <div className="ls-eyebrow"><Icon name={format.icon} size={16} />Turn into · {format.label}</div>
                        <h2 className="ls-turn-title" id={titleId}>
                            {status === 'done' ? (result.title || format.label) : isLoading ? `Writing your ${format.label.toLowerCase()}…` : format.label}
                        </h2>
                    </div>
                    <button type="button" className="ls-btn ls-btn-icon" onClick={onClose} aria-label="Close">
                        <Icon name="close" />
                    </button>
                </div>

                {isLoading && (
                    <div className="ls-turn-parts" role="status" aria-label={`Writing your ${format.label.toLowerCase()}`}>
                        {[0, 1, 2, 3].map(index => (
                            <div key={index} className="ls-turn-part">
                                <span className="ls-skeleton-text" style={{ width: '30%' }} />
                                <span className="ls-skeleton-text" style={{ width: '92%' }} />
                                <span className="ls-skeleton-text" style={{ width: '70%' }} />
                            </div>
                        ))}
                    </div>
                )}

                {status === 'error' && (
                    <Alert
                        severity="error"
                        action={<button type="button" className="ls-btn ls-btn-secondary ls-turn-retry" onClick={run}>Try again</button>}
                    >
                        {state.message}
                    </Alert>
                )}

                {status === 'done' && (
                    <>
                        <ol className="ls-turn-parts ls-stagger">
                            {result.parts.map((part, index) => (
                                <li key={index} className="ls-turn-part" style={{ '--i': index }}>
                                    <div className="ls-turn-part-head">
                                        <span className="ls-turn-part-label">
                                            {format.id === 'ad' || format.id === 'script'
                                                ? (part.heading || `${format.part} ${index + 1}`)
                                                : `${format.part} ${index + 1}`}
                                        </span>
                                        <CopyButton text={[format.id === 'ad' || format.id === 'script' ? '' : part.heading, part.body].filter(Boolean).join('\n')} />
                                    </div>
                                    {part.heading && format.id !== 'ad' && format.id !== 'script' && <div className="ls-turn-part-heading">{part.heading}</div>}
                                    <div className="ls-turn-part-body">{part.body}</div>
                                </li>
                            ))}
                        </ol>
                        {(result.cta || result.hashtags.length > 0) && (
                            <div className="ls-turn-extra">
                                {result.cta && <span className="ls-chip is-medium"><b>CTA</b>&nbsp;{result.cta}</span>}
                                {result.hashtags.map(tag => <span key={tag} className="ls-chip is-medium">{tag}</span>)}
                            </div>
                        )}
                    </>
                )}

                <div className="ls-turn-foot">
                    <button type="button" className="ls-btn ls-btn-ghost" onClick={run} disabled={isLoading}>
                        {isLoading ? <span className="ls-spinner" /> : <Icon name="refresh" size={18} />}Rewrite
                    </button>
                    <div className="ls-turn-foot-actions">
                        {status === 'done' && <CopyButton text={allText(result)} label="Copy all" />}
                        {open && status === 'done' && (
                            <button
                                type="button"
                                className="ls-btn ls-btn-primary"
                                onClick={() => navigate(open.path, { state: { seed: seedFor(result, source) } })}
                            >
                                <Icon name="open_in_new" size={18} />Open in {open.label}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

// source: { platform?, contentType?, title?, hook?, caption?, idea?, text? }; `exclude` hides formats (e.g. the screen's own)
const RepurposeMenu = ({ source, exclude = [], label = 'Turn into…', className = '', disabled = false }) => {
    const menuId = useId();
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState({ right: false, up: false });
    const [chosen, setChosen] = useState(null); // { format, source }, fixed while the modal is open
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);
    const formats = FORMATS.filter(item => !exclude.includes(item.id));

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        // First item gets focus so the menu works from the keyboard
        window.requestAnimationFrame(() => wrapRef.current?.querySelector('[role="menuitem"]')?.focus());
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const toggle = () => {
        if (!open) {
            // The menu opens to the left, or upward, when it wouldn't fit on screen
            const rect = buttonRef.current?.getBoundingClientRect();
            setPlacement({
                right: Boolean(rect) && rect.left + 240 > window.innerWidth - 16,
                up: Boolean(rect) && rect.bottom + 280 > window.innerHeight && rect.top > 300,
            });
        }
        setOpen(!open);
    };

    const onMenuKey = (e) => {
        if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
        e.preventDefault();
        const items = [...wrapRef.current.querySelectorAll('[role="menuitem"]')];
        const index = items.indexOf(document.activeElement);
        items[(index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
    };

    const choose = (item) => {
        setOpen(false);
        setChosen({ format: item, source: { ...source } });
    };

    const close = useCallback(() => {
        setChosen(null);
        buttonRef.current?.focus();
    }, []);

    return (
        <span className={`ls-turn ${className}`} ref={wrapRef}>
            <button
                type="button"
                className="ls-btn ls-btn-secondary"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                disabled={disabled}
                ref={buttonRef}
            >
                <Icon name="auto_mode" size={18} />{label}
            </button>
            {open && (
                <div className={`ls-popover ls-turn-menu${placement.right ? ' is-right' : ''}${placement.up ? ' is-up' : ''}`} role="menu" id={menuId} aria-label="Turn into" onKeyDown={onMenuKey}>
                    {formats.map(item => (
                        <button key={item.id} type="button" role="menuitem" className="ls-popover-row" onClick={() => choose(item)}>
                            <Icon name={item.icon} size={18} className="ls-turn-menu-icon" />
                            <span className="ls-turn-menu-text">
                                <span>{item.label}</span>
                                <span className="ls-turn-menu-hint">{item.hint}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
            {chosen && <RepurposeModal format={chosen.format} source={chosen.source} onClose={close} />}
        </span>
    );
};

export default RepurposeMenu;
